import React, { useEffect, useState, useRef } from 'react';
import { Bell, User, Users, LogOut, Settings, Briefcase, Users as UsersIcon, FileText, UserCircle, Sparkles, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function DashboardHeader({
  categories = [],
  onMenuClick,
  onNavigateToAICopilot,
  onNavigate,
  showMenuButton = false
}) {
  const navigateTo = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState<'EN' | 'FR'>('EN');
  const dropdownRef = useRef(null);
  const langDropdownRef = useRef(null);
  const categoryConfig = {
    business: {
      icon: <Briefcase className="w-4 h-4" data-id="element-124" />,
      label: 'Business',
      color: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    hr: {
      icon: <UsersIcon className="w-4 h-4" data-id="element-125" />,
      label: 'HR',
      color: 'bg-purple-50 text-purple-700 border-purple-200'
    },
    personal: {
      icon: <FileText className="w-4 h-4" data-id="element-126" />,
      label: 'Personal',
      color: 'bg-teal-50 text-teal-700 border-teal-200'
    }
  };
  const handleLawVoAI = () => {
    if (onNavigateToAICopilot) {
      onNavigateToAICopilot();
    }
  };
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  const handleNavigation = path => {
    console.log(`Navigating to: ${path}`);
    if (onNavigate) {
      onNavigate(path);
    }
    setIsDropdownOpen(false);
  };
  const handleNotifications = () => {
    console.log('Opening notifications');
  };
  const handleLogout = () => {
    console.log('Logging out');
    navigateTo('/login');
  };
  return <header className="bg-white border-b border-borderColor" data-id="element-127">
      <div className="flex justify-between items-center px-3 sm:px-6 py-3 sm:py-4" data-id="element-128">
        {/* Left side - Menu button (mobile) + Logo (mobile) + Categories */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0" data-id="element-129">
          {/* Mobile menu button - Shows on AI Copilot page on mobile */}
          {showMenuButton && <button onClick={onMenuClick} className="lg:hidden p-1.5 hover:bg-aliceBlue rounded-lg transition-colors cursor-pointer flex-shrink-0" aria-label="Open menu" data-id="element-130">
              <Menu className="w-5 h-5 text-secondary" data-id="element-131" />
            </button>}

          {/* Logo - visible on mobile, hidden on desktop - icon only */}
          <div className="lg:hidden flex-shrink-0 w-8 h-8 overflow-hidden" data-id="element-132">
            <img src="https://cdn.magicpatterns.com/uploads/xxZvTa8VY3udNLg3cdpVcE/LawVo-logo-icon.png" alt="LawVo" className="h-8 w-8 object-contain" data-id="element-133" />
          </div>

          {/* Categories - hidden on mobile when space is tight */}
          <div className="hidden sm:flex items-center gap-2" data-id="element-134">
            {categories.length > 0 ? categories.map(category => {
            const config = categoryConfig[category.toLowerCase()];
            if (!config) return null;
            return;
          }) : <span className="text-secondary text-xs sm:text-sm truncate" data-id="element-135"></span>}
          </div>
        </div>

        {/* Right side - Actions (disabled in TELUS triage mode) */}
        <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 pr-2 sm:pr-[30px] pointer-events-none opacity-50 select-none" aria-disabled="true" data-id="element-136">
          <button onClick={handleLawVoAI} className="px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-lg border-2 border-violetBlue bg-white hover:bg-aliceBlue transition-all cursor-pointer" data-id="element-137">
            <div className="flex items-center gap-1.5" data-id="element-138">
              <img src="https://cdn.magicpatterns.com/uploads/frDxN6VkzqMSZkD4nJP5uk/Screenshot_2025-12-31_at_11.06.59_AM.png" alt="LawVo AI" className="w-4 h-4 sm:w-5 sm:h-5 object-contain flex-shrink-0" data-id="element-139" />
              <span className="text-xs sm:text-sm font-semibold text-violetBlue whitespace-nowrap" data-id="element-140">
                LawVo AI
              </span>
            </div>
          </button>

          {/* Language Switcher */}
          <div className="relative flex-shrink-0" ref={langDropdownRef} data-id="element-141">
            <button onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-aliceBlue transition-colors cursor-pointer" data-id="element-142">
              {currentLang === 'EN' ? <span className="text-lg leading-none" data-id="element-143">🇨🇦</span> : <img src="https://cdn.magicpatterns.com/uploads/1suDiyBW4KtwhL9yhd6pvt/Flag_of_Quebec.svg.png" alt="FR" className="w-5 h-5 object-cover rounded-sm" data-id="element-144" />}
              <span className="text-xs sm:text-sm font-semibold text-primary" data-id="element-145">
                {currentLang}
              </span>
            </button>
            {isLangDropdownOpen && <div className="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-lg overflow-hidden z-20 border border-borderColor" data-id="element-146">
                <button onClick={() => {
              setCurrentLang('EN');
              setIsLangDropdownOpen(false);
            }} className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-aliceBlue transition-colors ${currentLang === 'EN' ? 'bg-aliceBlue' : ''}`} data-id="element-147">
                  <span className="text-lg leading-none" data-id="element-148">🇨🇦</span>
                  <span className="text-sm font-medium text-primary" data-id="element-149">EN</span>
                </button>
                <button onClick={() => {
              setCurrentLang('FR');
              setIsLangDropdownOpen(false);
            }} className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-aliceBlue transition-colors ${currentLang === 'FR' ? 'bg-aliceBlue' : ''}`} data-id="element-150">
                  <img src="https://cdn.magicpatterns.com/uploads/1suDiyBW4KtwhL9yhd6pvt/Flag_of_Quebec.svg.png" alt="FR" className="w-5 h-5 object-cover rounded-sm" data-id="element-151" />
                  <span className="text-sm font-medium text-primary" data-id="element-152">FR</span>
                </button>
              </div>}
          </div>

          <button className="relative cursor-pointer flex-shrink-0" onClick={handleNotifications} data-id="element-153">
            <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-secondary" data-id="element-154" />
            <span className="absolute -top-1 -right-1 bg-destructive rounded-full w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center text-white text-[10px] sm:text-xs" data-id="element-155">
              2
            </span>
          </button>
          <div className="relative flex-shrink-0" ref={dropdownRef} data-id="element-156">
            <button onClick={toggleDropdown} className="relative p-1 hover:bg-aliceBlue rounded-lg transition-colors cursor-pointer" data-id="element-157">
              <User className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" strokeWidth={1.5} data-id="element-158" />
              <span className="absolute top-0 right-0 rounded-full w-2 h-2 sm:w-2.5 sm:h-2.5 border-2 border-white" style={{
              backgroundColor: '#101D5B'
            }} data-id="element-159"></span>
            </button>
            {isDropdownOpen && <div className="absolute right-0 mt-2 w-56 sm:w-64 bg-white rounded-lg shadow-lg overflow-hidden z-10 border border-borderColor" data-id="element-160">
                <div className="p-3 sm:p-4 border-b border-borderColor" data-id="element-161">
                  <p className="text-base sm:text-lg font-semibold text-primary truncate" data-id="element-162">
                    John Doe
                  </p>
                  <p className="text-xs sm:text-sm text-secondary truncate" data-id="element-163">
                    ID: LV-DIGILITE-00045
                  </p>
                </div>
                <div className="py-2" data-id="element-164">
                  <a href="#" onClick={() => handleNavigation('/profile')} className="block px-3 sm:px-4 py-2.5 sm:py-3 text-primary hover:bg-aliceBlue transition-colors cursor-pointer" data-id="element-165">
                    <div className="flex items-center justify-between" data-id="element-166">
                      <div className="flex items-center min-w-0" data-id="element-167">
                        <UserCircle className="w-4 h-4 mr-2 flex-shrink-0" data-id="element-168" />
                        <span className="text-sm truncate" data-id="element-169">My Profile</span>
                      </div>
                      <span className="text-white text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ml-2" style={{
                    backgroundColor: '#101D5B'
                  }} data-id="element-170">
                        Complete
                      </span>
                    </div>
                  </a>
                  <a href="#" onClick={() => handleNavigation('/my-account')} className="block px-3 sm:px-4 py-2.5 sm:py-3 text-primary hover:bg-aliceBlue transition-colors cursor-pointer" data-id="element-171">
                    <div className="flex items-center" data-id="element-172">
                      <Settings className="w-4 h-4 mr-2 flex-shrink-0" data-id="element-173" />
                      <span className="text-sm" data-id="element-174">My Account</span>
                    </div>
                  </a>
                  <a href="#" onClick={() => handleNavigation('/members')} className="block px-3 sm:px-4 py-2.5 sm:py-3 text-primary hover:bg-aliceBlue transition-colors cursor-pointer" data-id="element-175">
                    <div className="flex items-center" data-id="element-176">
                      <Users className="w-4 h-4 mr-2 flex-shrink-0" data-id="element-177" />
                      <span className="text-sm" data-id="element-178">Members</span>
                    </div>
                  </a>
                </div>
                <div className="border-t border-borderColor" data-id="element-179">
                  <a href="#" onClick={handleLogout} className="block px-3 sm:px-4 py-2.5 sm:py-3 text-primary hover:bg-aliceBlue transition-colors cursor-pointer" data-id="element-180">
                    <div className="flex items-center" style={{
                  color: '#7E8299'
                }} data-id="element-181">
                      <LogOut className="w-4 h-4 mr-2 flex-shrink-0" data-id="element-182" />
                      <span className="text-sm" data-id="element-183">Log Out</span>
                    </div>
                  </a>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </header>;
}